/**
 * Date and Time Formatter Utility for TrophyCoin
 * Supports customizable date/time formats across templates, datasets, and mobile forms.
 */

export const DATE_TIME_FORMAT_PRESETS = [
  { label: 'Default / Raw Picker Value', value: '' },
  { label: 'YYYY/MM/DD (e.g. 2026/09/08)', value: 'YYYY/MM/DD' },
  { label: 'YYYY-MM-DD (e.g. 2026-09-08)', value: 'YYYY-MM-DD' },
  { label: 'DD/MM/YYYY (e.g. 08/09/2026)', value: 'DD/MM/YYYY' },
  { label: 'MM/DD/YYYY (e.g. 09/08/2026)', value: 'MM/DD/YYYY' },
  { label: 'D MMM YYYY (e.g. 8 Sep 2026)', value: 'D MMM YYYY' },
  { label: 'DD MMMM YYYY (e.g. 08 September 2026)', value: 'DD MMMM YYYY' },
  { label: 'MMMM (e.g. September)', value: 'MMMM' },
  { label: 'MMM (e.g. Sep)', value: 'MMM' },
  { label: 'MMMM YYYY (e.g. September 2026)', value: 'MMMM YYYY' },
  { label: 'YYYY (e.g. 2026)', value: 'YYYY' },
  { label: 'H:i - 24hr Time (e.g. 14:30)', value: 'H:i' },
  { label: 'HH:mm - 24hr Time (e.g. 14:30)', value: 'HH:mm' },
  { label: 'h:mm A - 12hr Time (e.g. 2:30 PM)', value: 'h:mm A' },
  { label: 'hh:mm A - 12hr Time (e.g. 02:30 PM)', value: 'hh:mm A' },
  { label: 'YYYY/MM/DD H:i (e.g. 2026/09/08 14:30)', value: 'YYYY/MM/DD H:i' },
  { label: 'YYYY-MM-DD HH:mm (e.g. 2026-09-08 14:30)', value: 'YYYY-MM-DD HH:mm' },
  { label: 'DD/MM/YYYY H:i (e.g. 08/09/2026 14:30)', value: 'DD/MM/YYYY H:i' },
  { label: 'D MMM YYYY, h:mm A (e.g. 8 Sep 2026, 2:30 PM)', value: 'D MMM YYYY, h:mm A' },
  { label: 'Custom Format...', value: 'custom' }
];

/**
 * Determine the appropriate HTML5 input picker type ('date', 'time', or 'datetime-local')
 * based on the field type, format string, and field label/alias.
 */
export const getPickerTypeForFormat = (fieldType, formatStr, label = '', alias = '') => {
  const fType = String(fieldType || '').toLowerCase();
  const fLabel = String(label || '').toLowerCase();
  const fAlias = String(alias || '').toLowerCase();
  const labelCombined = `${fLabel} ${fAlias}`;

  if (formatStr && formatStr !== 'custom') {
    const hasDateTokens = /[YMD]/.test(formatStr);
    const hasTimeTokens = /[HhimsAa]/.test(formatStr);
    if (hasTimeTokens && !hasDateTokens) return 'time';
    if (hasDateTokens && !hasTimeTokens) return 'date';
    if (hasDateTokens && hasTimeTokens) return 'datetime-local';
  }

  if (fType === 'time') return 'time';
  if (fType === 'date') return 'date';

  // Check if label or alias provides specific intent
  const labelHasTime = /\btime\b|hour|minute|clock/i.test(labelCombined) || /(?:^|_|\s)time(?:$|_|\s)/i.test(labelCombined);
  const labelHasDate = /\bdate\b|day|month|year|dob/i.test(labelCombined) || /(?:^|_|\s)date(?:$|_|\s)/i.test(labelCombined);

  if (labelHasTime && !labelHasDate) return 'time';
  if (labelHasDate && !labelHasTime) return 'date';

  if (fType === 'datetime' || fType === 'datetime-local' || (labelHasDate && labelHasTime)) {
    return 'datetime-local';
  }

  return null;
};

/**
 * Return a sensible default format if none is specified for a date/time field.
 */
export const getDefaultFormat = (fieldType, label = '', alias = '') => {
  const pickerType = getPickerTypeForFormat(fieldType, '', label, alias);
  if (pickerType === 'time') return 'H:i';
  if (pickerType === 'date') return 'YYYY/MM/DD';
  if (pickerType === 'datetime-local') return 'YYYY/MM/DD H:i';
  return '';
};

/**
 * Convert an arbitrary date/time string to the required standard value format
 * for HTML5 picker inputs ('YYYY-MM-DD', 'HH:mm', or 'YYYY-MM-DDTHH:mm').
 */
export const toPickerInputValue = (val, pickerType) => {
  if (!val || !pickerType) return '';
  const strVal = String(val).trim();

  // Try ISO / standard date regex (YYYY-MM-DD or YYYY/MM/DD, optional time)
  const dateMatch = strVal.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (dateMatch) {
    const y = dateMatch[1];
    const m = dateMatch[2].padStart(2, '0');
    const d = dateMatch[3].padStart(2, '0');
    const hh = (dateMatch[4] !== undefined ? dateMatch[4] : '00').padStart(2, '0');
    const mm = (dateMatch[5] !== undefined ? dateMatch[5] : '00').padStart(2, '0');
    if (pickerType === 'date') return `${y}-${m}-${d}`;
    if (pickerType === 'time') return `${hh}:${mm}`;
    if (pickerType === 'datetime-local') return `${y}-${m}-${d}T${hh}:${mm}`;
  }

  // Try time-only regex (HH:mm, HH:mm:ss, optional AM/PM)
  const timeMatch = strVal.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?$/i);
  if (timeMatch) {
    let h = parseInt(timeMatch[1], 10);
    const mm = timeMatch[2].padStart(2, '0');
    const ampm = timeMatch[4] ? timeMatch[4].toUpperCase() : null;
    if (ampm === 'PM' && h < 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    const hh = String(h).padStart(2, '0');
    if (pickerType === 'time') return `${hh}:${mm}`;
    if (pickerType === 'date') {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    }
    if (pickerType === 'datetime-local') {
      const now = new Date();
      const y = now.getFullYear();
      const mo = String(now.getMonth() + 1).padStart(2, '0');
      const da = String(now.getDate()).padStart(2, '0');
      return `${y}-${mo}-${da}T${hh}:${mm}`;
    }
  }

  // Try month-only name (e.g. "September" or "Sep")
  const monthNamesFull = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  const monthNamesShort = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const monthIdx = monthNamesFull.indexOf(strVal.toLowerCase());
  const monthShortIdx = monthIdx >= 0 ? monthIdx : monthNamesShort.indexOf(strVal.toLowerCase());
  if (monthShortIdx >= 0) {
    const now = new Date();
    const y = now.getFullYear();
    const mo = String(monthShortIdx + 1).padStart(2, '0');
    if (pickerType === 'date') return `${y}-${mo}-01`;
    if (pickerType === 'datetime-local') return `${y}-${mo}-01T00:00`;
  }

  // Fallback to standard Date object parsing
  const d = new Date(strVal);
  if (!isNaN(d.getTime())) {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    if (pickerType === 'date') return `${y}-${mo}-${da}`;
    if (pickerType === 'time') return `${hh}:${mm}`;
    if (pickerType === 'datetime-local') return `${y}-${mo}-${da}T${hh}:${mm}`;
  }

  return '';
};

/**
 * Format a raw date/time string, ISO timestamp, or Date object according to a format string.
 * Supports tokens: YYYY, YY, MMMM, MMM, MM, M, DD, D, HH, H, hh, h, mm, m, i, ss, s, A, a.
 */
export const formatDateTimeValue = (val, formatStr) => {
  if (!val || !formatStr || formatStr === 'custom') return val;
  let year = 2026, month = 0, day = 1, hours = 0, minutes = 0, seconds = 0;
  let parsed = false;

  // 1. Try ISO / Standard Date Regex (YYYY-MM-DD or YYYY/MM/DD, optional time)
  const dateMatch = String(val).match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (dateMatch) {
    year = parseInt(dateMatch[1], 10);
    month = parseInt(dateMatch[2], 10) - 1;
    day = parseInt(dateMatch[3], 10);
    hours = dateMatch[4] !== undefined ? parseInt(dateMatch[4], 10) : 0;
    minutes = dateMatch[5] !== undefined ? parseInt(dateMatch[5], 10) : 0;
    seconds = dateMatch[6] !== undefined ? parseInt(dateMatch[6], 10) : 0;
    parsed = true;
  } else {
    // 2. Try Time-only format (HH:mm, HH:mm:ss, optional AM/PM)
    const timeMatch = String(val).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?$/i);
    if (timeMatch) {
      let h = parseInt(timeMatch[1], 10);
      const m = parseInt(timeMatch[2], 10);
      const s = timeMatch[3] ? parseInt(timeMatch[3], 10) : 0;
      const ampm = timeMatch[4] ? timeMatch[4].toUpperCase() : null;
      if (ampm === 'PM' && h < 12) h += 12;
      if (ampm === 'AM' && h === 12) h = 0;
      const now = new Date();
      year = now.getFullYear();
      month = now.getMonth();
      day = now.getDate();
      hours = h;
      minutes = m;
      seconds = s;
      parsed = true;
    } else {
      // 3. Try Month-only name (e.g. "September" or "Sep")
      const monthNamesFull = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
      const monthNamesShort = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const strLower = String(val).trim().toLowerCase();
      const mIdx = monthNamesFull.indexOf(strLower);
      const mShortIdx = mIdx >= 0 ? mIdx : monthNamesShort.indexOf(strLower);
      if (mShortIdx >= 0) {
        month = mShortIdx;
        day = 1;
        year = new Date().getFullYear();
        parsed = true;
      } else {
        // 4. Fallback to Date parser
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
          year = d.getFullYear();
          month = d.getMonth();
          day = d.getDate();
          hours = d.getHours();
          minutes = d.getMinutes();
          seconds = d.getSeconds();
          parsed = true;
        }
      }
    }
  }

  if (!parsed) return val;

  const monthNamesFull = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthNamesShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const h12 = hours % 12 || 12;
  const tokens = {
    YYYY: String(year),
    YY: String(year).slice(-2),
    MMMM: monthNamesFull[month] || '',
    MMM: monthNamesShort[month] || '',
    MM: String(month + 1).padStart(2, '0'),
    M: String(month + 1),
    DD: String(day).padStart(2, '0'),
    D: String(day),
    HH: String(hours).padStart(2, '0'),
    H: String(hours),
    hh: String(h12).padStart(2, '0'),
    h: String(h12),
    mm: String(minutes).padStart(2, '0'),
    m: String(minutes),
    i: String(minutes).padStart(2, '0'), // PHP minute token
    ss: String(seconds).padStart(2, '0'),
    s: String(seconds),
    A: hours >= 12 ? 'PM' : 'AM',
    a: hours >= 12 ? 'pm' : 'am'
  };

  return formatStr.replace(/YYYY|YY|MMMM|MMM|MM|M|DD|D|HH|hh|H|h|mm|ss|m|s|i|A|a/g, (match) => {
    return tokens[match] !== undefined ? tokens[match] : match;
  });
};
